import { createMarketUid } from '../utils'
import { fetchIntrinsicYields } from './intrinsic'
import { fetchLenderRewards } from './rewards'
import {
  LenderRewards,
  LenderRewardsByMarketUid,
  YieldDataByMarketUid,
  YieldDataWithTimestamp,
} from './types'

export const fetchGeneralYields = async (): Promise<YieldDataWithTimestamp> => {
  const [intrinsicYields, rewards] = await Promise.all([
    fetchIntrinsicYields(),
    fetchLenderRewards(),
  ])
  return {
    intrinsicYields,
    lenderRewards: rewards.byLender,
    rewardsByReserveToken: rewards.byReserveToken,
  }
}

export const fetchGeneralYieldsByMarketUid =
  async (): Promise<YieldDataByMarketUid> => {
    const { intrinsicYields, lenderRewards, rewardsByReserveToken } =
      await fetchGeneralYields()

    return {
      intrinsicYields,
      lenderRewards: flattenLenderRewards(lenderRewards),
      // Same rewards, un-flattened: the flatten above assumes a uid ends in the
      // underlying address, which Aave V4 (reserve-id uids) breaks. Consumers
      // with market nodes join (lender, underlying) off this instead.
      rewardsByLenderAsset: lenderRewards,
      // Left un-flattened for the same reason: turning a reserve token into a
      // marketUid needs the market's own metadata, which only the consumer has.
      rewardsByReserveToken,
    }
  }

const flattenLenderRewards = (
  input: LenderRewards,
): LenderRewardsByMarketUid => {
  const result: LenderRewardsByMarketUid = {}

  for (const chainId in input) {
    const lenderMap = input[chainId]

    for (const lender in lenderMap) {
      const assetMap = lenderMap[lender]

      for (const asset in assetMap) {
        const reward = assetMap[asset]

        const marketUid = createMarketUid(chainId, lender, asset)

        result[marketUid] = reward
      }
    }
  }

  return result
}
